// ---------------------------------------------------------------------------
// Cross-Reference Index — builds Page ↔ Flow lookup indexes.
//
// Indexes are built from EditorStore (blocks, flows) and provide:
//   - flowUsageIndex: which elements reference a given flow
//   - elementEventIndex: which flows an element triggers
//   - actionUsageIndex: which flows use a given action type
// ---------------------------------------------------------------------------

import { foldBundleEventBindings } from "@cogo/publisher-core/event-binding-shape"

import type { CogoElement, CogoFlow, CogoBlockDef, CogoEventRef } from '@/features/page-editor/store/types'
import { findElementInTree } from '@/features/page-editor/store/canvas-slice'
import {
  walkFlowSteps,
  analyzeFlowStateUsage,
  extractNavigationEdges,
  type StateUsageEntry,
  type NavigationEntry,
} from '@/features/flow-editor/services/flow-analysis'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FlowUsageEntry {
  blockName: string
  elementId: string
  eventType: string
}

export interface ElementEventEntry {
  eventType: string
  flowName: string
}

export interface CrossReferenceIndex {
  /** flow name → list of elements that reference it */
  flowUsageIndex: Map<string, FlowUsageEntry[]>
  /** element id → list of event→flow bindings */
  elementEventIndex: Map<string, ElementEventEntry[]>
  /** action type → list of flow names that use it */
  actionUsageIndex: Map<string, string[]>
  /** flow name → state variable read/write entries */
  stateUsageIndex: Map<string, StateUsageEntry[]>
  /** flow name → navigation edges to pages */
  navigationIndex: Map<string, NavigationEntry[]>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Walk all elements in a tree, calling `fn` for each. */
function walkElements(root: CogoElement, fn: (el: CogoElement) => void) {
  fn(root)
  if (root.children) {
    for (const child of root.children) {
      walkElements(child, fn)
    }
  }
}

/** Extract the flow name from an event binding value (string or CogoEventRef). */
function resolveFlowName(binding: string | CogoEventRef): string | undefined {
  if (typeof binding === 'string') return binding
  return binding.flow ?? binding.action
}

/** Collect all action types used in a flow's steps (recursive). */
function collectActionTypes(steps: CogoFlow['steps']): string[] {
  const actions: string[] = []
  walkFlowSteps(steps, (step) => {
    if (step.type === 'basic' || step.type === 'expression') {
      actions.push(step.action)
    }
  })
  return actions
}

/** Summarize a flow's actions into a short string like "restApi → setState → showToast". */
export function summarizeFlowActions(flow: CogoFlow): string {
  const actions = collectActionTypes(flow.steps)
  if (actions.length === 0) return 'empty'
  // Deduplicate while preserving order
  const seen = new Set<string>()
  const unique: string[] = []
  for (const a of actions) {
    if (!seen.has(a)) {
      seen.add(a)
      unique.push(a)
    }
  }
  return unique.slice(0, 4).join(' → ') + (unique.length > 4 ? ' …' : '')
}

// ---------------------------------------------------------------------------
// Build index
// ---------------------------------------------------------------------------

export function buildCrossReferenceIndex(
  blocks: Record<string, CogoBlockDef>,
  flows: Record<string, CogoFlow>,
  pages: Array<{ id: string; route: string }> = [],
): CrossReferenceIndex {
  const flowUsageIndex = new Map<string, FlowUsageEntry[]>()
  const elementEventIndex = new Map<string, ElementEventEntry[]>()
  const actionUsageIndex = new Map<string, string[]>()
  const stateUsageIndex = new Map<string, StateUsageEntry[]>()
  const navigationIndex = new Map<string, NavigationEntry[]>()

  // --- 1. Scan event bindings ---
  // v2 BlockDef has `events: EventMap` (elementId → eventType → EventBinding)
  // v1 had events embedded in element tree nodes. Support both.
  // events.json format: { bindings: { "elementId:eventType": { flow: "name" } } }
  for (const [blockName, block] of Object.entries(blocks ?? {})) {
    const rawEvents = (block as unknown as Record<string, unknown>).events as Record<string, unknown> | undefined // TS2352 bridge — BlockDef.events shape varies between v1/v2 formats; runtime-discriminated
    if (!rawEvents || typeof rawEvents !== 'object') {
      // skip to v1 fallback below
    } else if ('bindings' in rawEvents || 'elements' in rawEvents) {
      // BOTH accepted spellings, folded into one flat `elementId:eventType`
      // map. Before Task 4 / authority-repair (2026-08-09, review round 2) this
      // branch required a literal `bindings` key, so an `elements`-shaped
      // artifact — what the agentic generator emits 357/357 times — fell
      // through to the v2 element-map branch below and was iterated as if
      // `"elements"` were an element id, producing garbage cross-references.
      const bindings = foldBundleEventBindings<string | CogoEventRef>(rawEvents)
      for (const [compoundKey, binding] of Object.entries(bindings)) {
        const colonIdx = compoundKey.lastIndexOf(':')
        if (colonIdx === -1) continue
        const elementId = compoundKey.slice(0, colonIdx)
        const eventType = compoundKey.slice(colonIdx + 1)
        const flowName = resolveFlowName(binding)
        if (!flowName) continue

        if (!flowUsageIndex.has(flowName)) flowUsageIndex.set(flowName, [])
        flowUsageIndex.get(flowName)!.push({ blockName, elementId, eventType })

        if (!elementEventIndex.has(elementId)) elementEventIndex.set(elementId, [])
        elementEventIndex.get(elementId)!.push({ eventType, flowName })
      }
    } else {
      // v2 path: block.events (EventMap = Record<elementId, Record<eventType, EventBinding>>)
      const eventMap = rawEvents as Record<string, Record<string, string | CogoEventRef>>
      for (const [elementId, eventsForElement] of Object.entries(eventMap)) {
        if (!eventsForElement || typeof eventsForElement !== 'object') continue
        for (const [eventType, binding] of Object.entries(eventsForElement)) {
          const flowName = resolveFlowName(binding)
          if (!flowName) continue

          if (!flowUsageIndex.has(flowName)) flowUsageIndex.set(flowName, [])
          flowUsageIndex.get(flowName)!.push({ blockName, elementId, eventType })

          if (!elementEventIndex.has(elementId)) elementEventIndex.set(elementId, [])
          elementEventIndex.get(elementId)!.push({ eventType, flowName })
        }
      }
    }

    // v1 fallback: block.root with element-level events
    const root = (block as unknown as Record<string, unknown>).root as CogoElement | undefined // TS2352 bridge — v1 BlockDef.root fallback; not in v2 BlockDef contract
    if (root) {
      walkElements(root, (el) => {
        if (!el.events) return
        for (const [eventType, binding] of Object.entries(el.events)) {
          const flowName = resolveFlowName(binding)
          if (!flowName) continue

          if (!flowUsageIndex.has(flowName)) flowUsageIndex.set(flowName, [])
          flowUsageIndex.get(flowName)!.push({ blockName, elementId: el.id, eventType })

          if (!elementEventIndex.has(el.id)) elementEventIndex.set(el.id, [])
          elementEventIndex.get(el.id)!.push({ eventType, flowName })
        }
      })
    }
  }

  // --- 2. Scan flows for action types ---
  for (const [flowName, flow] of Object.entries(flows ?? {})) {
    if (!flow?.steps) continue
    const actionTypes = collectActionTypes(flow.steps)
    for (const actionType of actionTypes) {
      if (!actionUsageIndex.has(actionType)) actionUsageIndex.set(actionType, [])
      const list = actionUsageIndex.get(actionType)!
      if (!list.includes(flowName)) list.push(flowName)
    }
  }

  // --- 3. Analyze state usage and navigation edges per flow ---
  for (const [flowName, flow] of Object.entries(flows ?? {})) {
    if (!flow?.steps) continue
    const stateEntries = analyzeFlowStateUsage(flow)
    if (stateEntries.length > 0) {
      stateUsageIndex.set(flowName, stateEntries)
    }
    const navEntries = extractNavigationEdges(flowName, flow, pages)
    if (navEntries.length > 0) {
      navigationIndex.set(flowName, navEntries)
    }
  }

  return { flowUsageIndex, elementEventIndex, actionUsageIndex, stateUsageIndex, navigationIndex }
}

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

export function getFlowUsages(index: CrossReferenceIndex, flowName: string): FlowUsageEntry[] {
  return index.flowUsageIndex.get(flowName) ?? []
}

export function getElementEvents(index: CrossReferenceIndex, elementId: string): ElementEventEntry[] {
  return index.elementEventIndex.get(elementId) ?? []
}

export function getActionUsages(index: CrossReferenceIndex, actionType: string): string[] {
  return index.actionUsageIndex.get(actionType) ?? []
}

// ---------------------------------------------------------------------------
// React hook — auto-rebuilds on store changes
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'

/** Returns a memoized CrossReferenceIndex that updates when blocks or flows change. */
export function useCrossReferenceIndex(): CrossReferenceIndex {
  const blocks = useEditorStore((s) => s.blocks)
  const flows = useEditorStore((s) => s.flows)
  const app = useEditorStore((s) => s.app)
  const pages = useMemo(
    () => (app?.pages ?? []).map((p) => ({ id: p.id, route: p.route })),
    [app],
  )
  return useMemo(() => buildCrossReferenceIndex(blocks, flows, pages), [blocks, flows, pages])
}
